import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from '../common/PersonaBadge';
import ComparisonTable from './ComparisonTable';
import { gymnasiumService } from '../../services/gymnasiumService';
import { RadarData } from '../../types/gymnasium';
import api from '../../services/api';
import { FiX, FiBarChart2, FiSearch, FiCheck } from 'react-icons/fi';
import GymSubHeader from './GymSubHeader';
import * as d3 from 'd3';

// Radar-specific palette — no pink, red, or green. Blues, purples, ambers, cyans.
const RADAR_COLORS = ['#7c3aed', '#2563eb', '#d97706', '#0891b2'];

const ComparativeRadar: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement>(null);
  const [personas, setPersonas] = useState<any[]>([]);
  // Restore selection from sessionStorage for persistence within gym
  const [selected, setSelected] = useState<string[]>(() => {
    try {
      const saved = sessionStorage.getItem('radar_selected');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [source, setSource] = useState<'capabilities' | 'gymnasium'>('gymnasium');
  const [radarData, setRadarData] = useState<RadarData | null>(null);
  const [loading, setLoading] = useState(false);
  const [topPersonas, setTopPersonas] = useState<{ persona_id: string; name: string; elo: number; matches: number }[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'elo' | 'matches'>('elo');

  useEffect(() => {
    const load = async () => {
      try {
        const [personaResp, rk, matches] = await Promise.all([
          api.get('/personas?limit=500'),
          gymnasiumService.getRankings(),
          gymnasiumService.listMatches(),
        ]);
        const data = personaResp.data?.personas || personaResp.data || [];
        setPersonas(data);

        const pidActivity: Record<string, { count: number; lastSeen: string; name: string; elo: number }> = {};
        for (const m of matches) {
          for (const pid of (m.persona_ids || [])) {
            const idx = m.persona_ids.indexOf(pid);
            const name = (m.persona_names || [])[idx] || pid.slice(0, 12);
            if (!pidActivity[pid]) pidActivity[pid] = { count: 0, lastSeen: '', name, elo: 1500 };
            pidActivity[pid].count++;
            if (m.created_at > pidActivity[pid].lastSeen) pidActivity[pid].lastSeen = m.created_at;
          }
        }
        for (const r of (rk.ratings || [])) {
          if (pidActivity[r.persona_id]) {
            pidActivity[r.persona_id].elo = r.elo;
            pidActivity[r.persona_id].name = r.name;
          } else {
            pidActivity[r.persona_id] = { count: 0, lastSeen: '', name: r.name, elo: r.elo };
          }
        }
        // Also include all personas (even those without matches)
        for (const p of data) {
          if (!pidActivity[p.id]) {
            pidActivity[p.id] = { count: 0, lastSeen: '', name: p.name, elo: 1500 };
          }
        }
        const sorted = Object.entries(pidActivity)
          .sort(([, a], [, b]) => b.elo - a.elo)
          .map(([pid, info]) => ({ persona_id: pid, name: info.name, elo: info.elo, matches: info.count }));
        setTopPersonas(sorted);

        // Auto-select top 2 ranked personas if nothing is saved
        const savedSel = sessionStorage.getItem('radar_selected');
        if (!savedSel || JSON.parse(savedSel).length === 0) {
          const ranked = sorted.filter(p => p.matches > 0);
          if (ranked.length >= 2) {
            const autoSel = ranked.slice(0, 2).map(p => p.persona_id);
            setSelected(autoSel);
            sessionStorage.setItem('radar_selected', JSON.stringify(autoSel));
          }
        }
      } catch (err) {
        console.error('Failed to load radar data:', err);
      }
    };
    load();
  }, []);

  // Canonical category mapping — bucket granular skills into broad axes
  const CANONICAL_MAP: Record<string, string> = {
    // Technical
    technical: 'Technical', coding: 'Technical', 'code review': 'Technical', debugging: 'Technical',
    'system architecture': 'Technical', 'infrastructure as code': 'Technical', engineering: 'Technical',
    programming: 'Technical', development: 'Technical', architecture: 'Technical',
    // Analytical
    analytical: 'Analytical', analysis: 'Analytical', 'problem solving': 'Analytical',
    'data analysis': 'Analytical', reasoning: 'Analytical', research: 'Analytical',
    // Creative
    creativity: 'Creative', creative: 'Creative', innovation: 'Creative', design: 'Creative',
    // Communication
    communication: 'Communication', writing: 'Communication', documentation: 'Communication',
    presentation: 'Communication', teaching: 'Communication', explanation: 'Communication',
    // Collaboration
    collaboration: 'Collaboration', teamwork: 'Collaboration', leadership: 'Collaboration',
    mentoring: 'Collaboration', facilitation: 'Collaboration',
    // Domain
    'domain expertise': 'Domain', domain: 'Domain', specialization: 'Domain',
    // Adaptability
    adaptability: 'Adaptability', flexibility: 'Adaptability', learning: 'Adaptability',
  };

  function normalizeRadarData(data: RadarData): RadarData {
    // Step 1: find shared categories (where 2+ personas have non-zero scores)
    const sharedCats = data.categories.filter(cat => {
      const nonZero = data.personas.filter(p => (p.scores[cat] || 0) > 0).length;
      return nonZero >= 2;
    });

    // If 3+ shared categories, use them directly
    if (sharedCats.length >= 3) {
      return { ...data, categories: sharedCats };
    }

    // Step 2: fall back to canonical mapping — bucket all capabilities into broad axes
    const canonicalPersonas = data.personas.map(p => {
      const buckets: Record<string, { sum: number; count: number }> = {};
      Object.entries(p.scores).forEach(([key, val]) => {
        const canonical = CANONICAL_MAP[key.toLowerCase()] || 'Other';
        if (!buckets[canonical]) buckets[canonical] = { sum: 0, count: 0 };
        buckets[canonical].sum += val;
        buckets[canonical].count++;
      });
      const scores: Record<string, number> = {};
      Object.entries(buckets).forEach(([cat, { sum, count }]) => {
        scores[cat] = Math.round((sum / count) * 10) / 10;
      });
      return { ...p, scores };
    });

    // Collect all canonical categories that have data from 2+ personas
    const allCats = new Set<string>();
    canonicalPersonas.forEach(p => Object.keys(p.scores).forEach(k => allCats.add(k)));
    const canonicalCats = Array.from(allCats).filter(cat => {
      return canonicalPersonas.filter(p => (p.scores[cat] || 0) > 0).length >= 2;
    }).filter(c => c !== 'Other');

    if (canonicalCats.length >= 3) {
      setToast('Capabilities normalized to common axes for comparison');
      setTimeout(() => setToast(null), 4000);
      return { ...data, categories: canonicalCats.sort(), personas: canonicalPersonas };
    }

    // Step 3: last resort — use all categories from union
    return data;
  }

  useEffect(() => {
    if (selected.length < 2) { setRadarData(null); return; }
    setLoading(true);
    gymnasiumService.getRadarData(selected, source)
      .then(data => {
        if (source === 'gymnasium' && data.categories.length === 0) {
          setSource('capabilities');
          setToast('No gauntlet run data — showing static capabilities instead');
          setTimeout(() => setToast(null), 4000);
          return;
        }
        setRadarData(normalizeRadarData(data));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selected, source]);

  // Draw D3 radar
  useEffect(() => {
    if (!radarData || !svgRef.current || radarData.categories.length === 0) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 420, height = 420;
    const cx = width / 2, cy = height / 2;
    const maxRadius = 165;
    const categories = radarData.categories;
    const n = categories.length;
    const angleSlice = (Math.PI * 2) / n;

    // Background rings with labels
    for (let level = 1; level <= 5; level++) {
      const r = (maxRadius / 5) * level;
      svg.append('circle')
        .attr('cx', cx).attr('cy', cy).attr('r', r)
        .attr('fill', 'none')
        .attr('stroke', darkMode ? '#2a2a3e' : '#e5e7eb')
        .attr('stroke-width', level === 5 ? 1 : 0.5);
      // Level label
      svg.append('text')
        .attr('x', cx + 4).attr('y', cy - r + 12)
        .attr('fill', darkMode ? '#555' : '#bbb').attr('font-size', 8)
        .text(`${level * 2}`);
    }

    // Axis lines + labels
    categories.forEach((cat, i) => {
      const angle = angleSlice * i - Math.PI / 2;
      const x2 = cx + maxRadius * Math.cos(angle);
      const y2 = cy + maxRadius * Math.sin(angle);
      svg.append('line')
        .attr('x1', cx).attr('y1', cy).attr('x2', x2).attr('y2', y2)
        .attr('stroke', darkMode ? '#333' : '#d1d5db').attr('stroke-width', 0.5);
      const lx = cx + (maxRadius + 25) * Math.cos(angle);
      const ly = cy + (maxRadius + 25) * Math.sin(angle);
      svg.append('text')
        .attr('x', lx).attr('y', ly)
        .attr('text-anchor', 'middle').attr('dominant-baseline', 'middle')
        .attr('fill', darkMode ? '#999' : '#555').attr('font-size', 10).attr('font-weight', 500)
        .text(cat.replace(/_/g, ' '));
    });

    // Draw each persona's polygon
    radarData.personas.forEach((p, pi) => {
      const color = RADAR_COLORS[pi % RADAR_COLORS.length];
      const points: [number, number][] = categories.map((cat, i) => {
        const val = Math.min((p.scores[cat] || 0) / 10, 1);
        const angle = angleSlice * i - Math.PI / 2;
        return [cx + maxRadius * val * Math.cos(angle), cy + maxRadius * val * Math.sin(angle)];
      });
      const pathData = points.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt[0]},${pt[1]}`).join(' ') + 'Z';

      svg.append('path')
        .attr('d', pathData)
        .attr('fill', color).attr('fill-opacity', 0.12)
        .attr('stroke', color).attr('stroke-width', 2.5);

      points.forEach(pt => {
        svg.append('circle')
          .attr('cx', pt[0]).attr('cy', pt[1]).attr('r', 4)
          .attr('fill', color).attr('stroke', darkMode ? '#1a1a2e' : '#fff').attr('stroke-width', 1.5);
      });
    });
  }, [radarData, darkMode]);

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = prev.includes(id) ? prev.filter(p => p !== id) : prev.length < 4 ? [...prev, id] : prev;
      sessionStorage.setItem('radar_selected', JSON.stringify(next));
      return next;
    });
  };

  // Sort and filter the persona list
  const filteredPersonas = topPersonas
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'elo') return b.elo - a.elo;
      return b.matches - a.matches;
    });

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode ? 'bg-gray-800 border-blue-600' : 'bg-white border-blue-500 shadow-sm'
  }`;

  return (
    <div className="space-y-4">
      <GymSubHeader
        icon={<FiBarChart2 className="w-4 h-4" />}
        title="Comparative Radar"
        subtitle={`${selected.length}/4 personas selected`}
        accentFrom="from-blue-400"
        accentTo="to-cyan-400"
      />

      {/* Two-column layout: persona list (left) + radar (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">

        {/* Left: persona selector list */}
        <div className={sectionClass}>
          <div className="flex items-center justify-between mb-3">
            <h2 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Personas ({selected.length}/4)
            </h2>
            <div className="flex gap-1">
              {(['elo', 'name', 'matches'] as const).map(s => (
                <button key={s} onClick={() => setSortBy(s)}
                  className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                    sortBy === s
                      ? darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'
                      : darkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {s === 'elo' ? 'Rating' : s === 'name' ? 'Name' : 'Matches'}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <FiSearch className={`absolute left-2.5 top-2 w-3.5 h-3.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              className={`w-full pl-8 pr-3 py-1.5 rounded-lg text-xs ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-800'
              } border focus:outline-none focus:ring-1 focus:ring-blue-500/30`}
            />
          </div>

          {/* Scrollable persona list */}
          <div className="space-y-1 max-h-[400px] overflow-y-auto">
            {filteredPersonas.map(p => {
              const isSelected = selected.includes(p.persona_id);
              const colorIdx = isSelected ? selected.indexOf(p.persona_id) : -1;
              const lineColor = colorIdx >= 0 ? RADAR_COLORS[colorIdx] : undefined;

              return (
                <button
                  key={p.persona_id}
                  onClick={() => toggleSelect(p.persona_id)}
                  disabled={!isSelected && selected.length >= 4}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-all ${
                    isSelected
                      ? darkMode ? 'bg-gray-700/60 ring-1' : 'bg-blue-50 ring-1'
                      : selected.length >= 4
                        ? 'opacity-30 cursor-not-allowed'
                        : darkMode ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'
                  }`}
                  style={isSelected && lineColor ? { ringColor: lineColor, borderColor: lineColor } : undefined}
                >
                  {/* Color indicator */}
                  {isSelected && lineColor && (
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: lineColor }} />
                  )}
                  {!isSelected && <div className="w-2 h-2 shrink-0" />}

                  <PersonaBadge personaId={p.persona_id} personaName={p.name} size="xs" clickable={false} />
                  <span className={`text-xs truncate flex-1 min-w-0 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {p.name.replace(/_/g, ' ')}
                  </span>
                  <span className={`text-[10px] tabular-nums shrink-0 ${darkMode ? 'text-amber-400/70' : 'text-amber-600/70'}`}>
                    {p.elo}
                  </span>
                  <span className={`text-[9px] tabular-nums shrink-0 w-6 text-right ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                    {p.matches}m
                  </span>
                  {isSelected && <FiCheck className="w-3 h-3 shrink-0 text-blue-400" />}
                </button>
              );
            })}
          </div>

          {/* Source toggle */}
          <div className="flex gap-2 mt-4 pt-3 border-t border-gray-700/30">
            <button
              onClick={() => setSource('capabilities')}
              className={`flex-1 px-3 py-1.5 text-xs rounded-lg transition-colors ${
                source === 'capabilities'
                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                  : darkMode ? 'bg-gray-700/40 text-gray-400' : 'bg-gray-100 text-gray-500'
              }`}
            >
              Static Capabilities
            </button>
            <button
              onClick={() => setSource('gymnasium')}
              className={`flex-1 px-3 py-1.5 text-xs rounded-lg transition-colors ${
                source === 'gymnasium'
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                  : darkMode ? 'bg-gray-700/40 text-gray-400' : 'bg-gray-100 text-gray-500'
              }`}
            >
              Arena Forged
            </button>
          </div>
        </div>

        {/* Right: radar chart */}
        <div>
          {selected.length >= 2 ? (
            <div className={sectionClass}>
              <div className="flex justify-center">
                {loading ? (
                  <div className={`w-[420px] h-[420px] rounded-lg animate-pulse ${darkMode ? 'bg-gray-700/30' : 'bg-gray-100'}`} />
                ) : radarData && radarData.categories.length > 0 ? (
                  <svg ref={svgRef} width={420} height={420} />
                ) : (
                  <div className={`w-[420px] h-[420px] flex items-center justify-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <p className="text-sm">{source === 'gymnasium' ? 'No gauntlet run data yet' : 'No capability data available'}</p>
                  </div>
                )}
              </div>
              {/* Legend */}
              {radarData && (
                <div className="flex justify-center gap-5 mt-4">
                  {radarData.personas.map((p, i) => (
                    <div key={p.persona_id} className="flex items-center gap-2">
                      <div className="w-3 h-1.5 rounded-full" style={{ backgroundColor: RADAR_COLORS[i % RADAR_COLORS.length] }} />
                      <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{p.name.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className={`${sectionClass} flex items-center justify-center`} style={{ minHeight: 420 }}>
              <div className="text-center">
                <FiBarChart2 className={`w-12 h-12 mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Select at least 2 personas to see the radar
                </p>
              </div>
            </div>
          )}

          {/* Comparison Table */}
          {radarData && radarData.categories.length > 0 && (
            <div className="mt-4">
              <ComparisonTable radarData={radarData} />
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50" style={{ animation: 'slideUp 0.3s ease-out' }}>
          <div className="flex items-center gap-3 px-6 py-3 rounded-xl shadow-2xl text-white bg-gradient-to-r from-amber-500 to-orange-500"
            style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
            <span className="text-xl">&#x1F4CA;</span>
            <span className="text-sm font-semibold">{toast}</span>
          </div>
        </div>
      )}
      <style>{`@keyframes slideUp { from { opacity:0; transform:translate(-50%,20px); } to { opacity:1; transform:translate(-50%,0); } }`}</style>
    </div>
  );
};

export default ComparativeRadar;
